import { Button } from "@/components/ui/button";
import { ClientLineChart } from "../../ClientLineChart";
import {
  useGetClientTokens,
  useRevokeClientToken,
} from "@/features/admin/api/client.queries";
import { formatTokenDate } from "@/lib/dateUtils";
import { Spinner } from "@/components/ui/spinner";

interface UsageProps {
  userId: string;
  clientName?: string;
}

export default function Usage({ userId, clientName }: UsageProps) {
  const {
    data: tokensData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetClientTokens(userId);

  const revokeMutation = useRevokeClientToken(userId);

  const handleRevokeToken = (apiKeyId: string) => {
    revokeMutation.mutate(apiKeyId);
  };

  if (isLoading) {
    return (
      <div className="bg-black-950 flex flex-col gap-5 rounded-lg p-5">
        <div className="flex w-full flex-col items-start justify-between space-y-5">
          {/* Header skeleton */}
          <div className="w-full space-y-2">
            <div className="h-5 w-24 animate-pulse rounded bg-white/10" />
            <div className="h-3 w-64 animate-pulse rounded bg-white/10" />
          </div>

          {/* Chart/Table skeleton */}
          <div className="flex w-full flex-col gap-2">
            <div className="h-3 w-24 animate-pulse rounded bg-white/10" />
            <div className="h-96 w-full animate-pulse rounded bg-white/10" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-black-950 flex flex-col gap-5 rounded-lg p-5">
        <div className="flex w-full flex-col items-start justify-between space-y-5">
          <div className="w-full space-y-2">
            <div className="text-lg leading-5 font-bold -tracking-wide text-white">
              Usage Statistics
            </div>
            <div className="text-black-500 text-xs leading-3">
              Manage API Keys for {clientName || "client"}
            </div>
          </div>
          <div className="flex h-64 w-full items-center justify-center">
            <div className="text-center">
              <i className="ri-error-warning-line mb-2 text-4xl text-red-500"></i>

              <p className="text-sm text-red-500">
                Failed to load tokens. Please try again.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Filter out Hash Key tokens for display
  const displayTokens =
    tokensData?.tokens.filter((token) => token.token_type !== "Hash Key") || [];

  if (!tokensData || displayTokens.length === 0) {
    return (
      <div className="bg-black-950 flex flex-col gap-5 rounded-lg p-5">
        <div className="flex w-full flex-col items-start justify-between space-y-5">
          <div className="w-full space-y-2">
            <div className="text-lg leading-5 font-bold -tracking-wide text-white">
              Endpoints
            </div>
            <div className="text-black-500 text-xs leading-3">
              Manage endpoints for {clientName || "client"}
            </div>
          </div>
          <div className="flex h-64 w-full items-center justify-center">
            <div className="text-center">
              <i className="ri-key-line text-black-500 mb-2 text-4xl"></i>
              <p className="text-black-500 text-sm">No API tokens found</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate total usage excluding Hash Key tokens
  const totalUsage = displayTokens.reduce(
    (sum, token) => sum + token.usage_count,
    0,
  );

  const endpointsData = displayTokens
    .map((token) => {
      const calculatedPercentage =
        totalUsage > 0 ? (token.usage_count / totalUsage) * 100 : 0;

      return {
        id: token.token_id,
        name: token.token_preview,
        value: token.usage_count,
        percentage: calculatedPercentage,
        createdDate: formatTokenDate(token.created),
        lastUsed: formatTokenDate(token.last_used),
        status: token.status,
        billing: token.billing || undefined,
      };
    })
    // Sort: Active tokens first (by usage desc), then inactive tokens (by usage desc)
    .sort((a, b) => {
      // If both have same status, sort by usage
      if (a.status === b.status) {
        return b.value - a.value;
      }
      // Active tokens come first
      return a.status === "Active" ? -1 : 1;
    });

  return (
    <div className="bg-black-950 flex flex-col gap-5 rounded-lg p-5">
      <div className="flex w-full flex-col items-start justify-between space-y-5">
        <div className="w-full space-y-2">
          <div className="flex w-full flex-row items-center justify-between">
            <div className="text-lg leading-5 font-bold -tracking-wide text-white">
              Usage Statistics
            </div>

            <Button
              className="gap-2 p-1.5 text-xs leading-normal font-medium tracking-normal text-white hover:bg-white/30 hover:text-white"
              variant="minimal"
              icon
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? (
                <Spinner size="md" className="stroke-primary!" />
              ) : (
                <i className="ri-loop-right-line text-md"></i>
              )}
            </Button>
          </div>

          <div className="text-black-500 text-xs leading-3">
            Manage API Keys for {clientName || "client"}
          </div>
        </div>
        <div className="flex w-full flex-col gap-2">
          <p className="text-black-500 text-xs leading-3 font-medium">
            API USAGE
          </p>
          <ClientLineChart
            endpoints={endpointsData}
            showTable={true}
            onRevokeToken={handleRevokeToken}
            onRevokeTokenLoading={revokeMutation.isPending}
          />
        </div>
      </div>
    </div>
  );
}
